import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { RootState } from "../../../../../../redux/store";
import { ShopRowData } from "../../types";
import { ScanToContext } from "..";
import { RubricResetButton } from "../shared/RubricResetButton";

type PropsType = {
    rowData?: ShopRowData;
};

type ActivityType = {
    id: string;
    title: string;
    icon: JSX.Element;
};

const PageHeader = styled.div`
    text-align: center;
    margin-bottom: 20px;
`;

const PageDescription = styled.p`
    color: #7f8c8d;
    font-size: 15px;
    margin: 0;
    line-height: 1.6;
`;

const ActivityContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 30px;
    justify-content: center;
    padding: 50px 20px 30px 20px;
`;

// Title on the left, per-rubric reset button on the right.
const SectionTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const ActivityCard = styled.button<{ isSelected: boolean; mainColor: string }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 240px;
    height: 200px;
    border: 2px solid ${({ isSelected, mainColor }) => (isSelected ? mainColor : "#e0e0e0")};
    border-radius: 16px;
    background-color: #ffffff;
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 25px;
    overflow: hidden;
    transform: ${({ isSelected }) => (isSelected ? "translateY(-4px)" : "translateY(0)")};

    &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: ${({ isSelected }) => (isSelected ? "6px" : "0")};
        background-color: ${({ mainColor }) => mainColor};
        transition: height 0.3s ease;
    }

    &:hover {
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
        transform: translateY(-4px);
    }

    &:focus {
        outline: none;
    }
`;

const IconWrapper = styled.div<{ mainColor: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;

    svg {
        width: 60px;
        height: 60px;
        fill: ${({ mainColor }) => mainColor};
        color: ${({ mainColor }) => mainColor};
    }
`;

const TitleText = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: #333;
    text-align: center;
`;

const AppNameSection = styled.div`
    max-width: 550px;
    margin: 50px auto 40px;
    padding: 30px 35px;
    background: linear-gradient(to bottom, #ffffff, #fafafa);
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border: 1px solid #f0f0f0;
`;

const AppNameHeader = styled.div`
    margin-bottom: 24px;
    text-align: left;
`;

const AppNameTitle = styled.h5`
    font-size: 18px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 10px 0;
    letter-spacing: -0.3px;
`;

const AppNameDescription = styled.p`
    font-size: 14px;
    color: #7f8c8d;
    margin: 0;
    line-height: 1.5;
`;

const AppNameInputGroup = styled.div<{ mainColor: string }>`
    display: flex;
    align-items: center;
    gap: 12px;
    background-color: white;
    padding: 8px 16px;
    border-radius: 12px;
    border: 2px solid #e8ecef;
    transition: all 0.3s ease;

    &:focus-within {
        border-color: ${({ mainColor }) => mainColor};
        box-shadow: 0 0 0 4px ${({ mainColor }) => mainColor}15;
    }
`;

const AppNamePrefix = styled.span<{ mainColor: string }>`
    font-size: 20px;
    font-weight: 700;
    color: ${({ mainColor }) => mainColor};
    white-space: nowrap;
    letter-spacing: -0.3px;
`;

const AppNameInput = styled.input<{ mainColor: string }>`
    flex: 1;
    padding: 10px 8px;
    font-size: 18px;
    font-weight: 600;
    border: none;
    background: transparent;
    color: #2c3e50;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
    }

    &::placeholder {
        color: #bdc3c7;
        font-weight: 400;
    }
`;

// Hamburger icon - simple filled style
const FastfoodIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22 10H2c0-5.5 4-9 10-9s10 3.5 10 9z" />
        <circle cx="7" cy="5" r="1" fill="white" />
        <circle cx="12" cy="4" r="1" fill="white" />
        <circle cx="17" cy="5" r="1" fill="white" />
        <rect x="2" y="11" width="20" height="2" rx="1" />
        <rect x="3" y="14" width="18" height="4" rx="1" />
        <path d="M3 19h18c0 2-2 4-9 4s-9-2-9-4z" />
    </svg>
);

// Fork and knife icon - simple filled style
const RestaurantIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z" />
    </svg>
);

// Coffee cup icon - simple filled style
const CafeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.5 3H6c-1.1 0-2 .9-2 2v5.71c0 3.83 2.95 7.18 6.78 7.29 3.96.12 7.22-3.06 7.22-7V8h.5c1.93 0 3.5-1.57 3.5-3.5S20.43 1 18.5 1h-1v2h1c.83 0 1.5.67 1.5 1.5S19.33 6 18.5 6h-.5V5c0-1.1-.9-2-2-2zM4 19h16v2H4z" />
    </svg>
);

// Hotel/Building icon - simple filled style
const HotelIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 2H5c-1.1 0-2 .9-2 2v18h18V4c0-1.1-.9-2-2-2zm-8 18H7v-3h4v3zm0-5H7v-2h4v2zm0-4H7V9h4v2zm0-4H7V5h4v2zm6 13h-4v-3h4v3zm0-5h-4v-2h4v2zm0-4h-4V9h4v2zm0-4h-4V5h4v2z" />
    </svg>
);

// Tent/Camping icon - simple filled style
const CampingIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 3L1 21h22L12 3zm0 4.5l6.5 11.5h-3.5v-4h-6v4H5.5L12 7.5z" />
    </svg>
);

export function Activite({ rowData }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const mainColor = useSelector(
        (state: RootState) => state.global.infoUser.maincolor
    );

    const { selectedActivity, setSelectedActivity, appName, setAppName } = React.useContext(ScanToContext);

    const iconColor = mainColor || "#5b7fc4"; // Use main color

    // Default app names for each activity type
    const defaultAppNames: Record<string, string> = {
        fastfood: "eat",
        traditional_restaurant: "eat",
        cafe: "eat",
        hotel: "be served",
        camping: "be served",
    };

    // Per-activity custom name overrides — kept across activity switches
    // within the same modal session so the user doesn't lose their edit
    // by clicking on another card. Seeded from the currently mounted
    // appName so the value present on first render counts as the
    // override for the active activity.
    const [appNameOverrides, setAppNameOverrides] = React.useState<
        Record<string, string>
    >(() =>
        defaultAppNames[selectedActivity] !== appName
            ? { [selectedActivity]: appName }
            : {}
    );

    const activities: ActivityType[] = [
        {
            id: "fastfood",
            title: t("Fastfood"),
            icon: <FastfoodIcon />,
        },
        {
            id: "traditional_restaurant",
            title: t("Traditional restaurant"),
            icon: <RestaurantIcon />,
        },
        {
            id: "cafe",
            title: t("Cafe"),
            icon: <CafeIcon />,
        },
        {
            id: "hotel",
            title: t("Hotel"),
            icon: <HotelIcon />,
        },
        {
            id: "camping",
            title: t("Camping"),
            icon: <CampingIcon />,
        },
    ];

    const handleSelectActivity = (activityId: string) => {
        setSelectedActivity(activityId);
        // Restore this activity's previously-typed override if any,
        // otherwise fall back to the activity's default name. This is
        // per-activity (not global) — typing "eat885" on Fastfood and
        // switching to Café shows "eat" for Café, but coming back to
        // Fastfood restores "eat885".
        const override = appNameOverrides[activityId];
        const defaultName = defaultAppNames[activityId] || "eat";
        setAppName(override ?? defaultName);
    };

    const handleAppNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setAppName(value);
        // Remember the override for the current activity so we can
        // restore it if the user switches away and comes back.
        setAppNameOverrides((prev) => ({
            ...prev,
            [selectedActivity]: value,
        }));
    };

    // Sync app name to preview windows via sessionStorage and BroadcastChannel
    React.useEffect(() => {
        // Save to sessionStorage
        sessionStorage.setItem('scanToAppName', appName);

        // Broadcast to external preview window
        const channel = new BroadcastChannel('scan-to-preview-channel');
        channel.postMessage({
            source: 'activite',
            appName: appName,
        });
        channel.close();
    }, [appName]);

    return (
        <div className="scan-to-content__clz" data-testid="scan-to-activity-container">
            <SectionTitleRow>
                <h4 className="scan-to-section-title__clz">{t("Activity")}</h4>
                <RubricResetButton section="activity" />
            </SectionTitleRow>
            <PageHeader>
                <PageDescription>
                    {t("Select your activity type")}
                </PageDescription>
            </PageHeader>
            <ActivityContainer data-testid="scan-to-activity-cards-container">
                {activities.map((activity) => (
                    <ActivityCard
                        key={activity.id}
                        data-testid={`scan-to-activity-card-${activity.id}`}
                        isSelected={selectedActivity === activity.id}
                        mainColor={mainColor || "#5b7fc4"}
                        onClick={() => handleSelectActivity(activity.id)}
                        type="button"
                    >
                        <IconWrapper mainColor={iconColor}>
                            {activity.icon}
                        </IconWrapper>
                        <TitleText>{activity.title}</TitleText>
                    </ActivityCard>
                ))}
            </ActivityContainer>

            {selectedActivity && (
                <AppNameSection data-testid="scan-to-activity-app-name-section">
                    <AppNameHeader>
                        <AppNameTitle>{t("Application name")}</AppNameTitle>
                        <AppNameDescription>
                            {t("Customize the name of your application")}
                        </AppNameDescription>
                    </AppNameHeader>
                    <AppNameInputGroup mainColor={mainColor || "#5b7fc4"}>
                        <AppNamePrefix mainColor={mainColor || "#5b7fc4"}>
                            Scan to
                        </AppNamePrefix>
                        <AppNameInput
                            data-testid="scan-to-activity-app-name-input"
                            mainColor={mainColor || "#5b7fc4"}
                            type="text"
                            value={appName}
                            onChange={handleAppNameChange}
                            placeholder={t("Enter application name")}
                        />
                    </AppNameInputGroup>
                </AppNameSection>
            )}
        </div>
    );
}
